import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
    DEFAULT_LIST_LIMIT,
    LIST_FILTER_PARAMS,
    LIST_SEARCH_DEBOUNCE_MS,
    clearLegacyDateParams,
    parseListLimit,
    readDateFiltersFromParams,
} from "../constants/listFilters";

function readFiltersFromParams(searchParams) {
    const { startDate, endDate } = readDateFiltersFromParams(searchParams);

    return {
        limit: parseListLimit(searchParams.get(LIST_FILTER_PARAMS.LIMIT)),
        startDate,
        endDate,
        search: searchParams.get(LIST_FILTER_PARAMS.SEARCH) || "",
    };
}

function applyFilterUpdates(prev, updates) {
    const next = new URLSearchParams(prev);

    if (updates.limit != null) {
        const limit = parseListLimit(updates.limit);
        if (limit === DEFAULT_LIST_LIMIT) next.delete(LIST_FILTER_PARAMS.LIMIT);
        else next.set(LIST_FILTER_PARAMS.LIMIT, String(limit));
    }

    if (updates.startDate != null) {
        const value = String(updates.startDate).trim();
        if (!value) next.delete(LIST_FILTER_PARAMS.FROM);
        else next.set(LIST_FILTER_PARAMS.FROM, value);
        clearLegacyDateParams(next);
    }

    if (updates.endDate != null) {
        const value = String(updates.endDate).trim();
        if (!value) next.delete(LIST_FILTER_PARAMS.TO);
        else next.set(LIST_FILTER_PARAMS.TO, value);
        clearLegacyDateParams(next);
    }

    if (updates.search != null) {
        const value = String(updates.search).trim();
        if (!value) next.delete(LIST_FILTER_PARAMS.SEARCH);
        else next.set(LIST_FILTER_PARAMS.SEARCH, value);
    }

    return next;
}

export function useUrlListFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlFilters = useMemo(() => readFiltersFromParams(searchParams), [searchParams]);

    const [searchInput, setSearchInput] = useState(urlFilters.search);
    const [debouncedSearch, setDebouncedSearch] = useState(urlFilters.search);

    useEffect(() => {
        setSearchInput(urlFilters.search);
        setDebouncedSearch(urlFilters.search);
    }, [urlFilters.search]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const trimmed = searchInput.trim();
            setDebouncedSearch(trimmed);

            setSearchParams(
                (prev) => {
                    const current = prev.get(LIST_FILTER_PARAMS.SEARCH) || "";
                    if (current === trimmed) return prev;
                    return applyFilterUpdates(prev, { search: trimmed });
                },
                { replace: true }
            );
        }, LIST_SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [searchInput, setSearchParams]);

    const setLimit = useCallback(
        (value) => {
            setSearchParams((prev) => applyFilterUpdates(prev, { limit: value }), {
                replace: true,
            });
        },
        [setSearchParams]
    );

    const setStartDate = useCallback(
        (value) => {
            setSearchParams((prev) => applyFilterUpdates(prev, { startDate: value }), {
                replace: true,
            });
        },
        [setSearchParams]
    );

    const setEndDate = useCallback(
        (value) => {
            setSearchParams((prev) => applyFilterUpdates(prev, { endDate: value }), {
                replace: true,
            });
        },
        [setSearchParams]
    );

    return {
        limit: urlFilters.limit,
        startDate: urlFilters.startDate,
        endDate: urlFilters.endDate,
        search: searchInput,
        debouncedSearch,
        setLimit,
        setStartDate,
        setEndDate,
        setSearch: setSearchInput,
    };
}
